import {
  DndContext as DragAndDropContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type Active,
  type DragOverEvent,
  type Over,
} from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { type Updater, functionalUpdate } from '@tanstack/react-table';
import { type VirtualItem } from '@tanstack/react-virtual';
import clsx from 'clsx';
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type JSX,
  type MutableRefObject,
} from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@dynatrace/strato-components/buttons';
import type { StylingProps } from '@dynatrace/strato-components/core';
import { Grid } from '@dynatrace/strato-components/layouts';
import { DragAllDirectionIcon } from '@dynatrace/strato-icons';

import { getRowOrderRowModel } from './row-order-model.js';
import {
  createRestrictToTableRowAreaModifier,
  getAllRowsInTableBody,
  getDragIndexes,
  resetAllStylesForCells,
  rowOrderCollisionDetection,
  transformCellsOnDragOver,
} from './row-order-utils.js';
import {
  dataTableV2DndHandleCSS,
  dataTableV2RowDndOverlayCSS,
} from './RowOrder.sty.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { getProperInertValue } from '../../../../core/utils/get-inert-value.js';
import { isDev } from '../../../../core/utils/is-node-environment.js';
import { DataTableV2DefaultCell } from '../../components/DataTableV2DefaultCell.js';
import { RowFragment } from '../../components/DataTableV2RowFragment.js';
import { useDataTableV2 } from '../../contexts/DataTableV2Context.js';
import type {
  Row,
  Table,
  TableOptions,
  TableFeature,
  ColumnDef,
  RowModel,
} from '../../hooks/useTable/types.js';
import type { DataTableV2RowData } from '../../public.api.js';
import { rowDetailsExpandedRowCellCSS } from '../RowDetails/RowDetails.sty.js';
import { RowDetailsElement } from '../RowDetails/RowDetailsElement.js';
import type { DataTableV2ThresholdsNormalizedRule } from '../Thresholds/normalize-rules.js';
import {
  dataTableV2HasThresholdsCSS,
  dataTableV2HasPillThresholdsCSS,
} from '../Thresholds/Thresholds.sty.js';

/**
 * Props for row order.
 * @public
 */
export type DataTableV2RowOrderProps<TData extends DataTableV2RowData> =
  | DataTableV2RowOrderControlledProps<TData>
  | DataTableV2RowOrderUncontrolledProps<TData>;

/**
 * Options for DataTableV2 row ordering.
 * @public
 */
export interface DataTableV2RowOrderBaseProps<
  TData extends DataTableV2RowData,
> {
  /**
   * Enables row ordering.
   * @defaultValue false
   */
  rowOrdering?:
    | boolean
    | {
        /**
         * When enabled, a drag handle is be rendered in the first column for every row.
         */
        enableDragAndDrop?: boolean;

        /**
         * Allows to disable the dragging of certain rows.
         */
        disableRowDragAndDrop?: (row: TData) => boolean;

        /**
         * Locks rows with disabled drag and drop in place if they are sorted to the start or end of the table.
         */
        lockDisabledRows?: boolean | 'start' | 'end';
      };

  /**
   * Callback triggered when row order is changed.
   * @param rowOrder - ordered set of row ids.
   */
  onRowOrderChange?: (rowOrder: string[]) => void;
}

/**
 * Controlled props for row order.
 * @public
 */
export interface DataTableV2RowOrderControlledProps<
  TData extends DataTableV2RowData,
> extends DataTableV2RowOrderBaseProps<TData> {
  /**
   * Ordered set of row ids.
   */
  rowOrder?: string[];
  defaultRowOrder?: never;
}

/**
 * Uncontrolled props for row order.
 * @public
 */
export interface DataTableV2RowOrderUncontrolledProps<
  TData extends DataTableV2RowData,
> extends DataTableV2RowOrderBaseProps<TData> {
  /**
   * Ordered set of row ids.
   */
  defaultRowOrder?: string[];
  rowOrder?: never;
}

/**
 * Extension interface to extend the types from the tanstack table instance.
 * @internal
 */
export interface DataTableV2RowOrderInstance<TData extends DataTableV2RowData> {
  /**
   * Internal storage for the row model with the changed order.
   * @internal
   */
  _getRowOrderRowModel?: () => RowModel<TData>;

  /**
   * Allows access to the row order model if available.
   */
  getRowOrderRowModel: () => RowModel<TData>;

  /**
   * Allows access to the row model which comes before the row order model.
   */
  getPreRowOrderRowModel: () => RowModel<TData>;

  /**
   * Sets or updates the `state.rowOrder` state.
   */
  setRowOrder: (updater: Updater<string[]>) => void;

  /**
   * Sets the currently dragged row's id.
   */
  setDraggingRowId: (rowId: string | undefined) => void;
}

/**
 * Extension interface for the table options.
 * @internal
 */
export interface DataTableV2RowOrderOptions<TData extends DataTableV2RowData> {
  /**
   * Enables or disables row ordering.
   */
  enableRowOrdering?: boolean;

  /**
   * Callback function fired when row order has changed.
   * @param rowOrder - new order of rows. Contains IDs of the rows.
   */
  onRowOrderChange?: (rowOrder: string[]) => void;

  /**
   * RowModel option for the row order model.
   */
  getRowOrderRowModel?: (table: Table<TData>) => () => RowModel<TData>;

  /**
   * - Enables drag and drop row ordering for all rows in the table OR
   * - A function that given a row, returns whether to enable/disable the drag and drop handle for that row
   */
  enableRowOrderDragAndDrop?: boolean | ((row: Row<TData>) => boolean);

  /**
   * Locks rows with disabled drag and drop in place if they are sorted to the start or end of the table.
   */
  lockDisabledRows?: boolean | 'start' | 'end';
}

/**
 * Extension interface to extend the types of the `Row` from the tanstack table.
 * @internal
 */
export interface DataTableV2RowOrderRow {
  /**
   *  Returns whether or not the row is draggable.
   */
  getCanDrag: () => boolean;

  /**
   * Flag determining if the row should be locked in place if drag and drop is disabled
   * and the row is sorted to the start or end of the table.
   */
  getDisabledRowDragAndDropLock: boolean | 'start' | 'end';
}

/** @internal */
export interface DataTableV2RowOrderState {
  /** Order of rows. */
  rowOrder: string[];
  draggingRowId: string | undefined;
}

/**
 * @internal
 */
export interface DataTableV2RowOrderingCellProps<TData> extends StylingProps {
  row: Row<TData>;
  rowIndex: number;
}

/**
 * Checks if the `rowOrdering` prop is a configuration object.
 * @param rowOrdering - The table prop that enables row ordering.
 * @returns `true` if the param is an object, `false` otherwise.
 */
function isRowOrderConfigObject<TData>(
  rowOrdering:
    | boolean
    | {
        enableDragAndDrop?: boolean;
        disableRowDragAndDrop?: (row: TData) => boolean;
      },
): rowOrdering is {
  enableDragAndDrop?: boolean;
  disableRowDragAndDrop?: (row: TData) => boolean;
} {
  return typeof rowOrdering === 'object';
}

/**
 * Configuration hook for the row order in the DataTableV2.
 * @internal
 */
export function useRowOrder<TData extends DataTableV2RowData>(
  props: DataTableV2RowOrderProps<TData>,
  options: TableOptions<TData>,
): void {
  const { rowOrdering } = props;

  const disableRowDragAndDropCallback = useCallback(
    (row: Row<TData>) => {
      if (
        rowOrdering !== undefined &&
        typeof rowOrdering !== 'boolean' &&
        rowOrdering.disableRowDragAndDrop
      ) {
        return !rowOrdering.disableRowDragAndDrop(row.original);
      }
      return true;
    },
    [rowOrdering],
  );

  const [rowOrder, onRowOrderChange] = useControllableState<string[]>({
    value: props.rowOrder,
    defaultValue: props.defaultRowOrder,
    onChange: props.onRowOrderChange,
  });

  options.enableRowOrderDragAndDrop = false;
  if (rowOrdering === false || rowOrdering === undefined) {
    options.enableRowOrdering = false;
    return;
  }

  options.enableRowOrdering = true;
  options.getRowOrderRowModel = getRowOrderRowModel();
  options.onRowOrderChange = onRowOrderChange;
  options.state = { ...options.state, rowOrder };

  // config object is given
  if (isRowOrderConfigObject(rowOrdering)) {
    if (
      rowOrdering.enableDragAndDrop === true &&
      rowOrdering.disableRowDragAndDrop !== undefined
    ) {
      options.lockDisabledRows = rowOrdering.lockDisabledRows;
      options.enableRowOrderDragAndDrop = disableRowDragAndDropCallback;
      options.autoResetPageIndex = false;
    } else {
      options.enableRowOrderDragAndDrop =
        rowOrdering.enableDragAndDrop === true;
    }
  }
}

const RowDragHandleCell = ({
  rowId,
  disabled,
}: {
  rowId: string;
  disabled: boolean;
}) => {
  const intl = useIntl();

  const { attributes, listeners } = useSortable({
    id: rowId,
  });

  const rowDragHandleLabel = intl.formatMessage(
    {
      defaultMessage: 'Drag to order row "{rowId}".',
      id: 'CrdM30eeqaF3mlZr',
      description: 'Label for the row drag handle.',
    },
    { rowId },
  );

  return (
    <DataTableV2DefaultCell>
      <Button
        size="condensed"
        variant="default"
        {...attributes}
        {...(!disabled ? listeners : undefined)}
        aria-disabled={disabled}
        aria-label={rowDragHandleLabel}
        className={dataTableV2DndHandleCSS({ disabled })}
      >
        <Button.Prefix>
          <DragAllDirectionIcon />
        </Button.Prefix>
      </Button>
    </DataTableV2DefaultCell>
  );
};

/**
 * Column definition for the row drag column.
 * @internal
 */
export const ROW_DRAG_COLUMN: ColumnDef<unknown, unknown> = {
  id: 'DataTableV2RowDragColumn',
  alignment: 'center',
  accessorFn: () => '',
  header: () => {
    return '';
  },
  width: 'content',
  minWidth: 24,
  enableSorting: false,
  enableResizing: false,
  enableUserActions: false,
  enableHiding: false,
  meta: {
    builtin: true,
    hasCustomCellRenderer: true,
    hasCustomHeaderRenderer: true,
    builtinPlacement: 'prefix',
  },
  cell: ({ row, table }) => (
    <RowDragHandleCell rowId={row.id} disabled={!row.getCanDrag()} />
  ),
};

/**
 * Helper function to identify the ROW_DRAG_COLUMN.
 * @internal
 */
export function isRowDragColumnDef<TData extends DataTableV2RowData>(
  column: ColumnDef<TData>,
): boolean {
  return column.id === ROW_DRAG_COLUMN.id;
}

/**
 * Defines the row order table feature.
 */
export const DataTableV2RowOrder: TableFeature = {
  createTable<TData>(table: Table<TData>) {
    /**
     * Allows access to the row model which comes before the row order.
     */
    table.getPreRowOrderRowModel = () => table.getExpandedRowModel();

    table.setDraggingRowId = (id) => {
      table.setState((state) => ({
        ...state,
        draggingRowId: id,
      }));
    };

    table.getRowOrderRowModel = () => {
      if (!table._getRowOrderRowModel && table.options.getRowOrderRowModel) {
        table._getRowOrderRowModel = table.options.getRowOrderRowModel(table);
      }

      if (!table._getRowOrderRowModel) {
        return table.getPreRowOrderRowModel();
      }

      return table._getRowOrderRowModel();
    };

    table.setRowOrder = (updater) => {
      const newValue = functionalUpdate(
        updater,
        table.getState().rowOrder ?? [],
      );
      table.options.onRowOrderChange?.(newValue);
    };
  },

  createRow<TData extends DataTableV2RowData>(
    row: Row<TData>,
    table: Table<TData>,
  ) {
    row.getCanDrag = () => {
      const { enableRowOrdering, enableRowOrderDragAndDrop } = table.options;
      const sortingState = table.getState().sorting;

      if (
        enableRowOrdering !== true ||
        // always disable the drag and drop handle if the rows are sorted
        (sortingState !== undefined && sortingState.length > 0)
      ) {
        return false;
      }

      // check if drag and drop is disabled for the row
      if (
        enableRowOrderDragAndDrop !== undefined &&
        typeof enableRowOrderDragAndDrop === 'function'
      ) {
        return enableRowOrderDragAndDrop(row);
      }
      return true;
    };

    row.getDisabledRowDragAndDropLock = table.options.lockDisabledRows ?? false;
  },
};

/**
 * @internal
 */
export function RowOrderingDragAndDropContext<
  TData extends DataTableV2RowData,
>({
  children,
  rows,
  virtualColumns,
  rowSeparation,
}: Readonly<{
  children: ReactNode;
  rows: Row<TData>[];
  virtualColumns: VirtualItem[];
  rowSeparation: 'none' | 'horizontalDividers' | 'zebraStripes';
}>) {
  const table = useDataTableV2();
  const tableHeaderHeight = table._getTotalHeaderHeight();
  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );
  const [draggingElement, setDraggingElement] = useState<ReactNode | undefined>(
    undefined,
  );
  const containedVariant = table.getVisualVariantOptions().contained;
  const virtualizationContainer = table.getVirtualizationContainer();

  const tableGridElement =
    virtualizationContainer?.querySelector<HTMLElement>('[role=grid]');

  const overlayContainer = useRef<HTMLDivElement>(null);

  const restrictToTableRowArea = useMemo(
    () =>
      createRestrictToTableRowAreaModifier(
        tableHeaderHeight,
        containedVariant,
        virtualizationContainer,
      ),
    [tableHeaderHeight, virtualizationContainer, containedVariant],
  );

  const rowOrderWhileDragging = useRef<string[]>([]);

  if (!tableGridElement || !virtualizationContainer) {
    return;
  }

  const overlayWidth = tableGridElement.parentElement?.clientWidth || 0;
  const originalRowOrder =
    table.getState().rowOrder ?? rows.map((row) => row.id);

  return (
    <DragAndDropContext
      sensors={sensors}
      collisionDetection={rowOrderCollisionDetection}
      modifiers={[restrictToTableRowArea]}
      onDragStart={({ active }: { active: Active }) => {
        if (active.data.current) {
          const element = createDraggingElement<TData>(
            overlayContainer,
            active,
            rows,
            virtualColumns,
            {
              width: overlayWidth,
              gridTemplateColumns: table.getTableGridTemplate(),
              subRowIndicatorColumnId: table.getSubRowIndicatorColumnId(),
              enableExpanding: table.options.enableExpanding,
              rowSeparation,
              hasThresholds: table.options.hasThresholds,
              rowThresholds: table.options.rowThresholds,
              rowDetailsTemplate: table.options.rowDetailsTemplate,
            },
          );
          setDraggingElement(element);

          // Use requestAnimationFrame to delay execution until just before the next paint,
          // ensuring the DOM is fully updated. This allows us to sync the overlay's scroll
          // position with the table grid's scroll state.
          requestAnimationFrame(() => {
            if (overlayContainer.current?.parentElement) {
              overlayContainer.current.parentElement.scrollLeft =
                tableGridElement?.parentElement?.scrollLeft ?? 0;
            }
          });

          table.setDraggingRowId(active.id as string);

          rowOrderWhileDragging.current = originalRowOrder
            ? [...originalRowOrder]
            : [];
        }
      }}
      onDragEnd={({ active, over }: { active: Active; over: Over | null }) => {
        table.setDraggingRowId(undefined);
        setDraggingElement(undefined);
        resetAllStylesForCells(getAllRowsInTableBody(tableGridElement));

        if (over === undefined || over === null) {
          if (isDev()) {
            // eslint-disable-next-line no-console
            console.log(
              '[Table] On drag end: over info is undefined or null',
              over,
            );
          }
          return;
        }

        // if dragged to the starting point, row order does not change
        if (over.id === active.id) {
          return;
        }

        const { fromIndex, toIndex } = getDragIndexes(
          originalRowOrder,
          active.id as string, // for us the IDs are always strings
          over.id as string, // for us the IDs are always strings
        );

        const newRowOrder = [...originalRowOrder];
        const activeRowSubRowsCount = (
          active.data.current?.row as Row<unknown>
        ).getLeafRows().length;
        const overRowSubRowsCount = (
          over.data.current?.row as Row<unknown>
        ).getLeafRows().length;

        const rowsToMove = newRowOrder.splice(
          fromIndex,
          1 + activeRowSubRowsCount,
        );

        newRowOrder.splice(
          fromIndex < toIndex
            ? toIndex + overRowSubRowsCount - activeRowSubRowsCount
            : toIndex,
          0,
          ...rowsToMove,
        );
        table.setRowOrder(newRowOrder);

        // In Safari, after scrolling a table while dragging, the cells are weirdly shifted in the grid
        // We did not find why, probably a safari bug. This fixes the issue for now.
        virtualizationContainer?.scrollBy({ top: 1, behavior: 'smooth' });
        virtualizationContainer?.scrollBy({ top: -1, behavior: 'smooth' });
      }}
      onDragOver={(event: DragOverEvent) => {
        transformCellsOnDragOver(
          tableGridElement,
          event,
          originalRowOrder,
          table.getVisualVariantOptions().rowSeparation === 'none',
        );
      }}
    >
      {children}
      <DragOverlay
        dropAnimation={null}
        className={dataTableV2RowDndOverlayCSS}
        style={{ width: overlayWidth, height: 'max-content' }}
      >
        {draggingElement}
      </DragOverlay>
    </DragAndDropContext>
  );
}

/**
 * Creates element to place in drag overlay.
 * @param active - The row being dragged. If no corresponding row is found,
 *                 then undefined is returned from this function.
 * @param rows - all rows.
 * @param virtualColumns - virtual items for the columns
 * @param rowConfig - object containing all necessary row configurations to be able to replicate the row for the drag overlay
 * @returns element intended for overlay
 */
function createDraggingElement<TData extends DataTableV2RowData>(
  overlayContainer: MutableRefObject<HTMLDivElement | null>,
  active: Active,
  rows: Row<TData>[],
  virtualColumns: VirtualItem[],
  rowConfig: {
    width: number;
    gridTemplateColumns: string;
    subRowIndicatorColumnId?: string | null;
    rowSeparation: 'none' | 'horizontalDividers' | 'zebraStripes';
    enableExpanding?: boolean;
    hasThresholds?: boolean;
    rowThresholds?: DataTableV2ThresholdsNormalizedRule<unknown>[];
    rowDetailsTemplate?: (params: { row: TData }) => ReactNode;
  },
): JSX.Element | undefined {
  const activeId = active.id as string;
  const draggingOriginalRow = active.data.current?.row as Row<TData>;
  const draggingVirtualRow = active.data.current?.virtualRow as VirtualItem;

  const {
    gridTemplateColumns,
    subRowIndicatorColumnId,
    rowSeparation,
    enableExpanding,
    hasThresholds,
    rowThresholds,
    rowDetailsTemplate,
  } = rowConfig;

  if (draggingOriginalRow === undefined || draggingVirtualRow === undefined) {
    if (isDev()) {
      // eslint-disable-next-line no-console
      console.log(
        `[DataTableV2] Dragging row or virtual row with row id ${activeId} is undefined.`,
      );
    }
    return;
  }

  const isRowDetailsOpen = draggingOriginalRow.getIsDetailsOpen();

  const draggingVirtualDetailsRow = {
    ...draggingVirtualRow,
    index: draggingVirtualRow.index + 1,
  };

  return (
    <Grid
      ref={overlayContainer}
      gridTemplateColumns={gridTemplateColumns}
      paddingBottom={0}
      minWidth="max-content"
      gap={0}
      // TODO: React 19 update - inert is then in the IntrinsicAttributes and can be passed directly
      {...{ inert: getProperInertValue(true) }}
      className={clsx({
        [dataTableV2HasThresholdsCSS]: hasThresholds,
        [dataTableV2HasPillThresholdsCSS]:
          hasThresholds &&
          rowThresholds?.some((threshold) => threshold.type === 'pill'),
        [`${rowDetailsExpandedRowCellCSS}`]: isRowDetailsOpen,
      })}
      style={{
        position: 'relative',
      }}
    >
      <RowFragment
        rows={rows}
        virtualRow={draggingVirtualRow}
        virtualColumns={virtualColumns}
        isInDragOverlay={true}
        subRowIndicatorColumnId={subRowIndicatorColumnId}
        enableExpanding={enableExpanding}
      />
      {isRowDetailsOpen ? (
        <RowDetailsElement
          originalRow={draggingOriginalRow}
          virtualRow={draggingVirtualDetailsRow}
          rowSeparation={rowSeparation}
          rowParity="even"
        >
          {rowDetailsTemplate?.({
            row: draggingOriginalRow.original,
          })}
        </RowDetailsElement>
      ) : null}
    </Grid>
  );
}
